import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarRange,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import styles from './PeriodFilter.module.css';
import {
  MONTHS,
  PRESETS,
  describe,
  isDefaultPeriod,
  isWholeMonth,
  isYmd,
  matchingPresetKey,
  monthOf,
  monthRange,
  shift,
  toYmd,
} from '../../../utils/period';

// The analytics period control. Two ways in — pick a month, or set a date
// range — plus quick presets and arrows that step one whole period at a time.
// It owns no data: it hands {from, to} up and the screen refetches.
const PeriodFilter = ({ period, onChange, loading, resolved }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('month');
  const [year, setYear] = useState(() => monthOf(period.from).y);
  const [draft, setDraft] = useState({ from: period.from, to: period.to });
  const rootRef = useRef(null);
  const today = useMemo(() => new Date(), []);
  const todayYmd = toYmd(today);

  const label = describe(period);
  const activePreset = matchingPresetKey(period, today);
  const isDefault = isDefaultPeriod(period, today);
  const monthMode = isWholeMonth(period);

  // Reopening should show the period actually in force, not whatever was last
  // half-typed into the range inputs.
  useEffect(() => {
    if (!open) return;
    setDraft({ from: period.from, to: period.to });
    setYear(monthOf(period.from).y);
    setTab(isWholeMonth(period) ? 'month' : 'range');
  }, [open, period]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (next) => {
    setOpen(false);
    if (next.from === period.from && next.to === period.to) return;
    onChange(next);
  };

  const applyDraft = () => {
    if (!isYmd(draft.from) || !isYmd(draft.to)) return;
    const ordered =
      draft.from <= draft.to
        ? draft
        : { from: draft.to, to: draft.from };
    apply(ordered);
  };

  const step = (direction) => onChange(shift(period, direction));

  // Nothing has happened after today, so stepping forward past the current
  // period would only ever show an empty dashboard.
  const canStepForward = period.to < todayYmd;

  const selectedMonth = monthMode ? monthOf(period.from) : null;
  const thisMonth = { y: today.getFullYear(), m: today.getMonth() + 1 };

  return (
    <div className={`dshPanel ${styles.root}`} ref={rootRef}>
      <div className={styles.lead}>
        <span className={`dshChip ${styles.chip}`}>
          <CalendarRange size={15} strokeWidth={2.2} />
        </span>
        <div className={styles.leadText}>
          <span className="dshEyebrow">Analytics period</span>
          <span className={styles.compare}>
            {resolved?.previous?.label
              ? `compared with ${resolved.previous.label}`
              : 'compared with the previous period'}
          </span>
        </div>
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.step}
          onClick={() => step(-1)}
          disabled={loading}
          title={monthMode ? 'Previous month' : 'Previous period'}
          aria-label={monthMode ? 'Previous month' : 'Previous period'}
        >
          <ChevronLeft size={15} strokeWidth={2.4} />
        </button>

        <button
          type="button"
          className={`${styles.current} ${open ? styles.currentOpen : ''}`}
          onClick={() => setOpen((v) => !v)}
          disabled={loading}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={`dshNum ${styles.currentLabel}`}>{label}</span>
          {resolved?.isToDate && <span className={styles.toDate}>to date</span>}
          <ChevronDown size={14} strokeWidth={2.4} className={styles.caret} />
        </button>

        <button
          type="button"
          className={styles.step}
          onClick={() => step(1)}
          disabled={loading || !canStepForward}
          title={monthMode ? 'Next month' : 'Next period'}
          aria-label={monthMode ? 'Next month' : 'Next period'}
        >
          <ChevronRight size={15} strokeWidth={2.4} />
        </button>

        {!isDefault && (
          <button
            type="button"
            className={`dshAction ${styles.reset}`}
            onClick={() => apply(PRESETS[0].range(today))}
            disabled={loading}
          >
            <RotateCcw size={13} strokeWidth={2.3} />
            This month
          </button>
        )}
      </div>

      {open && (
        <div className={styles.pop} role="dialog" aria-label="Choose analytics period">
          <div className={styles.presets}>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`${styles.preset} ${
                  activePreset === p.key ? styles.presetOn : ''
                }`}
                onClick={() => apply(p.range(today))}
              >
                {activePreset === p.key && <Check size={11} strokeWidth={3} />}
                {p.label}
              </button>
            ))}
          </div>

          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'month'}
              className={`${styles.tab} ${tab === 'month' ? styles.tabOn : ''}`}
              onClick={() => setTab('month')}
            >
              Month
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'range'}
              className={`${styles.tab} ${tab === 'range' ? styles.tabOn : ''}`}
              onClick={() => setTab('range')}
            >
              Date range
            </button>
          </div>

          {tab === 'month' ? (
            <div className={styles.monthPane}>
              <div className={styles.yearRow}>
                <button
                  type="button"
                  className={styles.step}
                  onClick={() => setYear((y) => y - 1)}
                  aria-label="Previous year"
                >
                  <ChevronLeft size={14} strokeWidth={2.4} />
                </button>
                <span className={`dshNum ${styles.year}`}>{year}</span>
                <button
                  type="button"
                  className={styles.step}
                  onClick={() => setYear((y) => y + 1)}
                  disabled={year >= today.getFullYear()}
                  aria-label="Next year"
                >
                  <ChevronRight size={14} strokeWidth={2.4} />
                </button>
              </div>

              <div className={styles.months}>
                {MONTHS.map((name, i) => {
                  const m = i + 1;
                  const future =
                    year > thisMonth.y || (year === thisMonth.y && m > thisMonth.m);
                  const on =
                    selectedMonth && selectedMonth.y === year && selectedMonth.m === m;
                  const isNow = year === thisMonth.y && m === thisMonth.m;
                  return (
                    <button
                      key={name}
                      type="button"
                      className={`${styles.month} ${on ? styles.monthOn : ''} ${
                        isNow ? styles.monthNow : ''
                      }`}
                      disabled={future}
                      onClick={() => apply(monthRange(year, m))}
                      title={future ? 'Not reached yet' : `${name} ${year}`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={styles.rangePane}>
              <label className={styles.field}>
                <span className="dshEyebrow">From</span>
                <input
                  type="date"
                  className={styles.input}
                  value={draft.from}
                  max={todayYmd}
                  onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                />
              </label>
              <label className={styles.field}>
                <span className="dshEyebrow">To</span>
                <input
                  type="date"
                  className={styles.input}
                  value={draft.to}
                  min={draft.from}
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                />
              </label>

              <div className={styles.rangeFoot}>
                <span className={styles.draftLabel}>
                  {isYmd(draft.from) && isYmd(draft.to)
                    ? describe(
                        draft.from <= draft.to
                          ? draft
                          : { from: draft.to, to: draft.from }
                      )
                    : 'Pick both dates'}
                </span>
                <button
                  type="button"
                  className={styles.apply}
                  onClick={applyDraft}
                  disabled={!isYmd(draft.from) || !isYmd(draft.to)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeriodFilter;
